import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { AppMenu, Flex, Text } from '@ghq-abi/design-system';

import { BackArrowIcon } from '~/shared/components/icons';
import { useNav } from '~/shared/contexts';

import cheersMenuLogo from '~/../public/img/cheers_menu_logo.png';

import {
  HelpCenterBar,
  HelpCenterContent
} from '../../../../../shared/components/HelpCenter';
import { useMenuListScroll } from '../../hooks/useMenuListScroll';

export function DesktopMenu() {
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuHeaderItem,
    menuItems,
    toggleAppMenu,
    checkIfIsActive,
    toggleSubMenu
  } = useNav();

  const { elementRef, isScrollable } = useMenuListScroll();

  const handleMenuItemClick = () => {
    toggleSubMenu(null);
    toggleAppMenu();
  };

  return (
    <AppMenu open={isAppMenuOpen}>
      <AppMenu.Trigger asChild>
        <AppMenu.Bar openMenu={toggleAppMenu} openMenuDelay={300}>
          <AppMenu.Header css={{ p: 0 }}>
            <AppMenu.MenuItem
              css={{ p: '$3 $2' }}
              leftIcon={menuHeaderItem.icon}
              isTriggerItem
            />
          </AppMenu.Header>
          <AppMenu.MenuList>
            {menuItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                leftIcon={item.icon}
                isActive={checkIfIsActive(item.path, item.routeComponents)}
                isTriggerItem
              />
            ))}
          </AppMenu.MenuList>
          <HelpCenterBar />
        </AppMenu.Bar>
      </AppMenu.Trigger>
      <AppMenu.Content
        closeMenuDelay={300}
        css={{
          justifyContent: 'flex-start',
          '> div': {
            '&:nth-last-child(2)': {
              height: 'calc($full - $appHeaderHeight) !important',
              mb: '$1'
            }
          }
        }}
        closeMenu={() => {
          toggleAppMenu();
          toggleSubMenu(null);
        }}>
        <AppMenu.Header css={{ p: 0 }}>
          <AppMenu.MenuItem
            title={menuHeaderItem.title}
            hasSubmenu
            css={{ p: '$3 $2' }}
            leftIcon={menuHeaderItem.icon}
            isSubMenuOpen={subMenuOpen?.key === menuHeaderItem.key}
            onClick={() => toggleSubMenu(menuHeaderItem)}
            renderSubmenu={
              <AppMenu.SubMenu
                title={menuHeaderItem.title || ''}
                alignWithHeader
                hideTrigger
                renderTitle={
                  <Flex align="center" gap="md" css={{ height: '$full' }}>
                    <AppMenu.MenuIconButton
                      onClick={() => toggleSubMenu(null)}
                      icon={<BackArrowIcon />}
                    />
                    <Text css={{ fontWeight: '$medium' }}>
                      {menuHeaderItem.title}
                    </Text>
                  </Flex>
                }>
                <AppMenu.MenuList>
                  {menuHeaderItem.routeComponents?.map(subitem => (
                    <AppMenu.MenuItem
                      key={subitem.key}
                      title={subitem.title}
                      route={subitem.path}
                      leftIcon={subitem.icon}
                      onClick={handleMenuItemClick}
                      isSubMenuItem
                    />
                  ))}
                </AppMenu.MenuList>
              </AppMenu.SubMenu>
            }
          />
        </AppMenu.Header>
        <AppMenu.MenuList
          isMainList
          ref={elementRef}
          bottomElement={
            <Image src={cheersMenuLogo} placeholder="blur" alt="Cheers Hub" />
          }
          bottomElementCss={{
            mb: '-$16',
            width: isScrollable
              ? 'calc($appMenuOpenWidth - $2)'
              : '$appMenuOpenWidth'
          }}>
          {menuItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              route={item.path}
              title={item.title}
              leftIcon={item.icon}
              as={Link}
              hasSubmenu={!!item.routeComponents?.length}
              isSubMenuOpen={subMenuOpen?.key === item.key}
              onHover={() => toggleSubMenu(item)}
              onHoverDelay={300}
              isActive={checkIfIsActive(item.path, item.routeComponents)}
              renderSubmenu={
                <AppMenu.SubMenu
                  title={item.title || ''}
                  css={{ borderRadius: '$4' }}>
                  <AppMenu.MenuList>
                    {item.routeComponents?.map(subitem => (
                      <AppMenu.MenuItem
                        key={subitem.key}
                        title={subitem.title}
                        route={subitem.path}
                        as={Link}
                        leftIcon={subitem.icon}
                        hasSubmenu={!!subitem.routeComponents?.length}
                        onHover={() => {}}
                        onHoverDelay={300}
                        onClick={
                          !subitem.routeComponents?.length
                            ? handleMenuItemClick
                            : undefined
                        }
                        isSubMenuItem
                        renderSubmenu={
                          subitem.routeComponents?.length ? (
                            <AppMenu.SubMenu
                              title={subitem.title || ''}
                              css={{ borderRadius: '$4' }}>
                              <AppMenu.MenuList>
                                {subitem.routeComponents.map(nestedItem => (
                                  <AppMenu.MenuItem
                                    key={nestedItem.key}
                                    title={nestedItem.title}
                                    route={nestedItem.path}
                                    as={Link}
                                    leftIcon={nestedItem.icon}
                                    onClick={handleMenuItemClick}
                                    isSubMenuItem
                                  />
                                ))}
                              </AppMenu.MenuList>
                            </AppMenu.SubMenu>
                          ) : undefined
                        }
                      />
                    ))}
                  </AppMenu.MenuList>
                </AppMenu.SubMenu>
              }
            />
          ))}
        </AppMenu.MenuList>
        <HelpCenterContent />
      </AppMenu.Content>
    </AppMenu>
  );
}
